<!-- UI-10: annotations-page tile (design: ims-annotation-panel). Isolated from the
     viewer — only used by AnnotationGrid on the project Annotations page. Fetches its
     own crop thumbnail (like AnnotationPreview) and renders the design card with a
     hover overlay (open · view-in-slide · delete) and a selection ring/badge. -->
<template>
<div class="ac-card" :class="{selected}" @click="$emit('open', annot)">
  <div class="ac-thumb">
    <img v-if="cropUrl" :src="cropUrl" alt="annotation" />
    <div v-else class="ac-spin"><i class="fas fa-spinner fa-spin"></i></div>

    <div v-if="selected" class="ac-ring"></div>
    <span class="ac-dot" :style="{background: termColor}"></span>

    <!-- selection badge (always visible when selected) -->
    <button v-if="selected" class="ac-sel on" :title="$t('button-cancel')" @click.stop="$emit('toggle', annot)">
      <i class="fas fa-check"></i>
    </button>

    <div class="ac-overlay">
      <button v-if="!selected" class="ac-sel" :title="$t('select')" @click.stop="$emit('toggle', annot)"></button>
      <div class="ac-actions">
        <button class="ac-act" :title="$t('open-preview')" @click.stop="$emit('open', annot)">
          <i class="fas fa-expand"></i>
        </button>
        <button class="ac-act" :title="$t('open-in-slide')" @click.stop="$emit('slide', annot)">
          <i class="fas fa-external-link-alt"></i>
        </button>
        <button class="ac-act ac-del" :title="$t('button-delete')" @click.stop="$emit('delete', annot)">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>
  </div>

  <div v-if="showMeta" class="ac-meta">
    <div class="ac-meta-top">
      <span class="ac-dot sm" :style="{background: termColor}"></span>
      <span class="ac-term">{{ termName }}</span>
      <span class="ac-n">#{{ annot.id }}</span>
    </div>
    <div class="ac-image">{{ imageName }}</div>
    <div class="ac-by">{{ authorName }} · {{ Number(annot.created) | moment('ll') }}</div>
  </div>
</div>
</template>

<script>
import {Ims} from '@/api';

export default {
  name: 'annotation-card',
  props: {
    annot: {type: Object, required: true},
    size: {type: Number, default: 150},
    color: {type: String, default: ''},
    showMeta: {type: Boolean, default: true},
    selected: {type: Boolean, default: false},
    allTerms: {type: Array, default: () => []},
    allUsers: {type: Array, default: () => []},
    allImages: {type: Array, default: () => []},
    blindMode: {type: Boolean, default: false}
  },
  data() {
    return {
      cropUrl: null
    };
  },
  computed: {
    firstTerm() {
      if (!this.annot.term || !this.annot.term.length) {
        return null;
      }
      return this.allTerms.find(t => this.annot.term.includes(t.id)) || null;
    },
    termColor() {
      return this.firstTerm ? this.firstTerm.color : '#9aa3b0';
    },
    termName() {
      return this.firstTerm ? this.firstTerm.name : this.$t('no-term');
    },
    authorName() {
      let user = this.allUsers.find(u => u.id === this.annot.user);
      return user ? user.fullName : '';
    },
    imageName() {
      if (this.annot.instanceFilename) {
        return this.annot.instanceFilename;
      }
      let image = this.allImages.find(i => i.id === this.annot.image);
      if (!image) {
        return '';
      }
      return (this.blindMode && image.blindedName) ? image.blindedName : image.instanceFilename;
    },
    cropParameters() {
      let params = {square: true, complete: true, thickness: 2, increaseArea: 1.25};
      if (this.color) {
        params.draw = true;
        params.color = `0x${this.color}`;
      }
      return params;
    }
  },
  methods: {
    async fetchThumbnail() {
      try {
        let url = this.annot.annotationCropURL(Math.max(this.size * 2, 256), 'jpg', this.cropParameters);
        let response = await Ims.instance.api.get(url, {responseType: 'blob'});
        if (this.cropUrl) {
          URL.revokeObjectURL(this.cropUrl);
        }
        this.cropUrl = URL.createObjectURL(response.data);
      } catch (error) {
        console.log(error);
      }
    }
  },
  mounted() {
    this.fetchThumbnail();
  },
  beforeDestroy() {
    if (this.cropUrl) {
      URL.revokeObjectURL(this.cropUrl);
    }
  }
};
</script>

<style scoped>
.ac-card {
  position: relative;
  border: 1px solid #e8eaef;
  border-radius: 11px;
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  transition: box-shadow 0.16s, transform 0.16s, border-color 0.16s;
}
.ac-card:hover {
  box-shadow: 0 10px 24px rgba(20, 30, 55, 0.15);
  transform: translateY(-2px);
  border-color: #d2d9f2;
}
.ac-thumb {
  position: relative;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #f1ecf3;
}
.ac-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ac-spin { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: #b9c0cc; }
.ac-ring { position: absolute; inset: 0; box-shadow: inset 0 0 0 3px #1e63d0; pointer-events: none; }
.ac-dot {
  position: absolute;
  top: 7px;
  right: 7px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  box-shadow: 0 0 0 2px #fff;
}
.ac-dot.sm { position: static; box-shadow: none; width: 9px; height: 9px; flex: none; }
.ac-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(15, 20, 35, 0.62), rgba(15, 20, 35, 0) 58%);
  opacity: 0;
  transition: opacity 0.16s;
}
.ac-card:hover .ac-overlay { opacity: 1; }
.ac-sel {
  position: absolute;
  top: 7px;
  left: 7px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.7px solid rgba(255, 255, 255, 0.95);
  background: rgba(20, 28, 46, 0.32);
  cursor: pointer;
  z-index: 4;
}
.ac-sel:hover { background: #1e63d0; border-color: #1e63d0; }
.ac-sel.on {
  border: none;
  background: #1e63d0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.3);
  font-size: 11px;
}
.ac-actions {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 8px;
  display: flex;
  justify-content: center;
  gap: 6px;
}
.ac-act {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #2a3342;
  font-size: 13px;
}
.ac-act:hover { background: #fff; }
.ac-del { color: #d8456a; }
.ac-meta { padding: 9px 11px 11px; }
.ac-meta-top { display: flex; align-items: center; gap: 7px; }
.ac-term { font-size: 12.5px; font-weight: 700; color: #1a2233; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ac-n { margin-left: auto; font-size: 11px; color: #aeb6c2; font-weight: 700; font-variant-numeric: tabular-nums; }
.ac-image { margin-top: 5px; font-size: 11.5px; color: #6b7480; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ac-by { margin-top: 2px; font-size: 11px; color: #9aa3b0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
